import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, Save, ArrowLeft, Search, X, Upload, Loader2, Camera } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { socialAPI, teamsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import ImageCropper from '../components/ImageCropper';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'LB', 'DB', 'K', 'P'];

const EditProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    positions: [],
    height_cm: '',
    weight_kg: '',
    location: '',
    avatar_url: '',
    favorite_team_id: '',
    favorite_player_ids: []
  });
  const [teamsList, setTeamsList] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]); // Array of profile objects
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [searchingPlayers, setSearchingPlayers] = useState(false);
  const [experiences, setExperiences] = useState([]);
  const [newExp, setNewExp] = useState({ team: '', league: '', season: '', position: '', achievements: '' });

  // Initialize names from user display_name
  const [firstName, setFirstName] = useState(() => {
    if (user?.display_name) {
      const parts = user.display_name.split(' ');
      return parts[0] || '';
    }
    return '';
  });
  const [lastName, setLastName] = useState(() => {
    if (user?.display_name) {
      const parts = user.display_name.split(' ');
      return parts.slice(1).join(' ') || '';
    }
    return '';
  });

  // Profile Picture States
  const [imageSrc, setImageSrc] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const { updateProfile } = useAuth(); // Destructure updateProfile

  useEffect(() => {
    loadProfile();
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await teamsAPI.getMinimalTeams();
      setTeamsList(response.data);
    } catch (error) {
      console.error('Failed to fetch teams', error);
    }
  };

  const loadProfile = async () => {
    try {
      const response = await socialAPI.getProfile(user.username);
      const data = response.data;
      setProfile({
        bio: data.bio || '',
        positions: data.positions || [],
        height_cm: data.height_cm || '',
        weight_kg: data.weight_kg || '',
        location: data.location || '',
        avatar_url: data.avatar_url || '',
        favorite_team: data.favorite_team || '',
        favorite_team_id: data.favorite_team_id || '',
        favorite_player_ids: data.favorite_player_ids || []
      });
      setSelectedPlayers(data.favorite_players || []);
      setExperiences(data.experiences || []);
    } catch (error) {
      // New profile
    } finally {
      setLoading(false);
    }
  };

  const handlePlayerSearch = async (query) => {
    setPlayerSearchQuery(query);
    if (query.length < 2) {
      setPlayerSearchResults([]);
      return;
    }

    setSearchingPlayers(true);
    try {
      const response = await socialAPI.searchProfiles(query);
      // Filter out teams and already selected players or self
      const players = response.data.filter(p =>
        p.type === 'user' &&
        p.id !== user.id &&
        !profile.favorite_player_ids.includes(p.id)
      );
      setPlayerSearchResults(players);
    } catch (error) {
      console.error('Player search failed', error);
    } finally {
      setSearchingPlayers(false);
    }
  };

  const addFavoritePlayer = (player) => {
    if (profile.favorite_player_ids.length >= 3) {
      toast({
        title: t('common.error'),
        description: t('profile.maxPlayersError'),
        variant: "destructive",
      });
      return;
    }

    setProfile(prev => ({
      ...prev,
      favorite_player_ids: [...prev.favorite_player_ids, player.id]
    }));
    setSelectedPlayers(prev => [...prev, player]);
    setPlayerSearchQuery('');
    setPlayerSearchResults([]);
  };

  const removeFavoritePlayer = (playerId) => {
    setProfile(prev => ({
      ...prev,
      favorite_player_ids: prev.favorite_player_ids.filter(id => id !== playerId)
    }));
    setSelectedPlayers(prev => prev.filter(p => p.id !== playerId));
  };

  // Image Cropper Handlers
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setIsCropperOpen(true);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedBlob) => {
    setIsCropperOpen(false);
    setUploadingAvatar(true);

    try {
      const response = await socialAPI.uploadAvatar(croppedBlob);
      const newAvatarUrl = response.data.avatar_url;

      setProfile(prev => ({ ...prev, avatar_url: newAvatarUrl }));
      updateProfile({
        profile: {
          ...user.profile,
          avatar_url: newAvatarUrl
        }
      });

      toast({ title: t('profile.photoUpdated') });
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      toast({ title: t('profile.photoUpdateError'), variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
      setImageSrc(null);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await socialAPI.updateProfile({
        ...profile,
        display_name: `${firstName.trim()} ${lastName.trim()}`,
        height_cm: profile.height_cm ? parseInt(profile.height_cm) : null,
        weight_kg: profile.weight_kg ? parseInt(profile.weight_kg) : null
      });

      // Update local user context if needed, though usually handled by reload or separate fetch
      updateProfile({ display_name: `${firstName.trim()} ${lastName.trim()}` });

      toast({ title: t('profile.saved') });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const togglePosition = (pos) => {
    setProfile(p => ({
      ...p,
      positions: p.positions.includes(pos)
        ? p.positions.filter(x => x !== pos)
        : [...p.positions, pos]
    }));
  };

  const handleAddExperience = async () => {
    if (!newExp.team.trim()) {
      toast({ title: t('profile.teamNamePlaceholder'), variant: 'destructive' });
      return;
    }
    try {
      const response = await socialAPI.addExperience(newExp);
      setExperiences([...experiences, response.data]);
      setNewExp({ team: '', league: '', season: '', position: '', achievements: '' });
      toast({ title: t('profile.experienceAdded') });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  const handleDeleteExperience = async (id) => {
    try {
      await socialAPI.deleteExperience(id);
      setExperiences(experiences.filter(e => e.id !== id));
      toast({ title: t('profile.experienceDeleted') });
    } catch (error) {
      toast({ title: t('common.error'), variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400">{t('common.loading')}</div>;
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-gray-400">
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t('common.back')}
      </Button>

      <h1 className="text-2xl font-bold text-white mb-6">{t('profile.editTitle')}</h1>

      {/* Basic Info */}
      <Card className="bg-[#1a1a1b] border-gray-800 mb-6">
        <CardHeader>
          <CardTitle className="text-white">{t('profile.basicInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-gray-300 mb-2 block">{t('profile.avatarUrl')}</Label>
            <div className="flex items-center gap-6">
              <div className="relative group">
                <Avatar className="h-24 w-24 border-2 border-gray-700">
                  <AvatarImage src={profile.avatar_url} />
                  <AvatarFallback className="bg-gray-700 text-2xl">{user.username?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>

                {uploadingAvatar && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <input
                  type="file"
                  id="avatar-upload"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploadingAvatar}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('avatar-upload').click()}
                  disabled={uploadingAvatar}
                  className="bg-transparent border-gray-700 text-white hover:bg-gray-800"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  {t('profile.changePhoto')}
                </Button>
                <p className="text-xs text-gray-500">{t('profile.photoUploadDesc')}</p>
              </div>
            </div>
          </div>


          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">{t('auth.firstName')}</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="bg-[#2a2a2b] border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">{t('auth.lastName')}</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="bg-[#2a2a2b] border-gray-700 text-white mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-gray-300">{t('profile.bio')}</Label>
            <Textarea
              value={profile.bio}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              placeholder={t('profile.bioPlaceholder')}
              className="bg-[#2a2a2b] border-gray-700 text-white"
              maxLength={500}
            />
          </div>

          {user.role !== 'standard' && (
            <>
              <div>
                <Label className="text-gray-300">{t('profile.positions')}</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {POSITIONS.map(pos => (
                    <Button
                      key={pos}
                      type="button"
                      variant={profile.positions.includes(pos) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => togglePosition(pos)}
                      className={profile.positions.includes(pos) ? 'bg-blue-600' : ''}
                    >
                      {pos}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('profile.height')}</Label>
                  <Input
                    type="number"
                    value={profile.height_cm}
                    onChange={(e) => setProfile({ ...profile, height_cm: e.target.value })}
                    className="bg-[#2a2a2b] border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('profile.weight')}</Label>
                  <Input
                    type="number"
                    value={profile.weight_kg}
                    onChange={(e) => setProfile({ ...profile, weight_kg: e.target.value })}
                    className="bg-[#2a2a2b] border-gray-700 text-white"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <Label className="text-gray-300">{t('profile.location')}</Label>
            <Input
              value={profile.location}
              onChange={(e) => setProfile({ ...profile, location: e.target.value })}
              placeholder={t('profile.locationPlaceholder')}
              className="bg-[#2a2a2b] border-gray-700 text-white"
              maxLength={100}
            />
          </div>

          {(user.role === 'standard' || user.role === 'admin') && (
            <>
              <div>
                <Label className="text-gray-300">{t('profile.favoriteTeam')}</Label>
                <Select
                  value={profile.favorite_team_id || "none"}
                  onValueChange={(val) => setProfile({ ...profile, favorite_team_id: val === "none" ? null : val })}
                >
                  <SelectTrigger className="bg-[#2a2a2b] border-gray-700 text-white">
                    <SelectValue placeholder={t('profile.selectTeam')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e1f] border-gray-700 text-white">
                    <SelectItem value="none">{t('common.none')}</SelectItem>
                    {teamsList.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          {t.logo_url && <img src={t.logo_url} alt="" className="h-4 w-4 object-contain rounded" />}
                          {t.name}
                        </div>
                      </SelectItem>
                    ))}

                    {/* Fallback for legacy text string if it doesn't match a formal team ID yet */}
                    {profile.favorite_team && !profile.favorite_team_id && (
                      <SelectItem value="legacy" disabled>
                        ({t('common.legacy')}: {profile.favorite_team})
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1 italic">{t('profile.selectTeamDesc')}</p>
              </div>

              <div className="space-y-3">
                <Label className="text-gray-300">{t('profile.favoritePlayersLabel')}</Label>

                {/* Selected Players */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedPlayers.map(p => (
                    <div key={p.id} className="flex items-center gap-2 bg-blue-600/20 border border-blue-500/30 pl-1.5 pr-2.5 py-1 rounded-full group">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={p.avatar_url} />
                        <AvatarFallback className="bg-blue-600 text-[10px]">{p.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-blue-400 font-medium">{p.display_name || p.username}</span>
                      <button onClick={() => removeFavoritePlayer(p.id)} className="text-blue-400 hover:text-red-400 transition-colors ml-1">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Search Input */}
                {profile.favorite_player_ids.length < 3 && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      value={playerSearchQuery}
                      onChange={(e) => handlePlayerSearch(e.target.value)}
                      placeholder={t('profile.searchPlayersPlaceholder')}
                      className="bg-[#2a2a2b] border-gray-700 text-white pl-10"
                    />

                    {/* Search Results */}
                    {playerSearchResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-[#1e1e1f] border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                        {playerSearchResults.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => addFavoritePlayer(p)}
                            className="w-full flex items-center gap-3 p-3 hover:bg-gray-800 text-left transition-colors border-b border-gray-800 last:border-0"
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={p.avatar_url} />
                              <AvatarFallback>{p.username?.[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium text-white">{p.display_name || p.username}</p>
                              <p className="text-xs text-gray-400">@{p.username}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {searchingPlayers && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                        {t('common.searching') || 'Aranıyor...'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}



          <Button onClick={handleSaveProfile} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            <Save className="h-4 w-4 mr-2" />
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </CardContent>
      </Card>

      {/* Experiences */}
      {
        user.role !== 'standard' && (
          <Card className="bg-[#1a1a1b] border-gray-800">
            <CardHeader>
              <CardTitle className="text-white">{t('profile.experience')}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Existing experiences */}
              {experiences.length > 0 && (
                <div className="space-y-3 mb-6">
                  {experiences.map(exp => (
                    <div key={exp.id} className="flex items-center justify-between bg-[#2a2a2b] p-3 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{exp.team}</p>
                        <p className="text-gray-400 text-sm">
                          {[exp.position, exp.league, exp.season].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteExperience(exp.id)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new experience */}
              <div className="space-y-3 border-t border-gray-700 pt-4">
                <h4 className="text-gray-300 font-medium">{t('profile.addExperience')}</h4>
                <Input
                  value={newExp.team}
                  onChange={(e) => setNewExp({ ...newExp, team: e.target.value })}
                  placeholder={t('profile.teamNamePlaceholder')}
                  className="bg-[#2a2a2b] border-gray-700 text-white"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={newExp.league}
                    onChange={(e) => setNewExp({ ...newExp, league: e.target.value })}
                    placeholder={t('profile.league')}
                    className="bg-[#2a2a2b] border-gray-700 text-white"
                  />
                  <Input
                    value={newExp.season}
                    onChange={(e) => setNewExp({ ...newExp, season: e.target.value })}
                    placeholder={t('profile.season')}
                    className="bg-[#2a2a2b] border-gray-700 text-white"
                  />
                  <Input
                    value={newExp.position}
                    onChange={(e) => setNewExp({ ...newExp, position: e.target.value })}
                    placeholder={t('profile.position')}
                    className="bg-[#2a2a2b] border-gray-700 text-white"
                  />
                </div>
                <Textarea
                  value={newExp.achievements}
                  onChange={(e) => setNewExp({ ...newExp, achievements: e.target.value })}
                  placeholder={t('profile.achievementsPlaceholder')}
                  className="bg-[#2a2a2b] border-gray-700 text-white"
                />
                <Button onClick={handleAddExperience} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('profile.addExperienceBtn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )
      }

      <ImageCropper
        open={isCropperOpen}
        imageSrc={imageSrc}
        onCropComplete={handleCropComplete}
        onCancel={() => setIsCropperOpen(false)}
      />
    </div >
  );
};

export default EditProfile;
